import { DEFAULT_COLLAPSED_GROUPS, type FolderGroupId, type FolderSort } from './folderModel';

/**
 * Discovery sidebar preferences, remembered per browser. Every read and write
 * tolerates blocked storage: the preference still applies for this visit.
 */
const KEYS = {
  groups: 'discovery-sidebar-groups',
  width: 'discovery-sidebar-width',
  collapsed: 'discovery-sidebar-collapsed',
  sort: 'discovery-sidebar-sort',
  owner: 'discovery-sidebar-owner',
} as const;

export const SIDEBAR_MIN_WIDTH = 208;
export const SIDEBAR_MAX_WIDTH = 440;
export const SIDEBAR_DEFAULT_WIDTH = 256;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage blocked.
  }
}

const GROUP_IDS: FolderGroupId[] = ['pinned', 'active', 'complete', 'archived'];

export function readCollapsedGroups(): Set<FolderGroupId> {
  const raw = read(KEYS.groups);
  if (raw === null) return new Set(DEFAULT_COLLAPSED_GROUPS);
  try {
    const parsed: unknown = JSON.parse(raw);
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v): v is FolderGroupId => GROUP_IDS.includes(v as FolderGroupId)) : DEFAULT_COLLAPSED_GROUPS,
    );
  } catch {
    return new Set(DEFAULT_COLLAPSED_GROUPS);
  }
}

export function writeCollapsedGroups(groups: Set<FolderGroupId>): void {
  write(KEYS.groups, JSON.stringify([...groups]));
}

export function clampWidth(w: number): number {
  if (!Number.isFinite(w)) return SIDEBAR_DEFAULT_WIDTH;
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(w)));
}

export function readWidth(): number {
  const raw = read(KEYS.width);
  return raw === null ? SIDEBAR_DEFAULT_WIDTH : clampWidth(Number(raw));
}

export function writeWidth(w: number): void {
  write(KEYS.width, String(clampWidth(w)));
}

export function readCollapsed(): boolean {
  return read(KEYS.collapsed) === '1';
}

export function writeCollapsed(v: boolean): void {
  write(KEYS.collapsed, v ? '1' : '0');
}

export function readSort(): FolderSort {
  const raw = read(KEYS.sort);
  return raw === 'name' || raw === 'open' || raw === 'recent' ? raw : 'recent';
}

export function writeSort(s: FolderSort): void {
  write(KEYS.sort, s);
}

export function readOwner(): string {
  return read(KEYS.owner) ?? '';
}

export function writeOwner(o: string): void {
  write(KEYS.owner, o);
}
